export interface EnvelopeChannel {
  name: string;
  yMin: Float32Array;
  yMax: Float32Array;
}

export interface WebGLRenderInput {
  channels: EnvelopeChannel[];
  width: number;
  height: number;
  xOffset?: number;
  color?: [number, number, number, number];
  clear?: [number, number, number, number];
}

function createShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
): WebGLShader | null {
  if (typeof gl.createShader !== "function") return null;
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function createProgram(gl: WebGL2RenderingContext): WebGLProgram | null {
  if (
    typeof gl.createProgram !== "function" ||
    typeof gl.attachShader !== "function" ||
    typeof gl.linkProgram !== "function"
  ) {
    return null;
  }
  const vertex = createShader(
    gl,
    gl.VERTEX_SHADER,
    `#version 300 es
    precision highp float;
    in vec2 a_position;
    uniform vec2 u_resolution;
    void main() {
      vec2 zeroToOne = a_position / u_resolution;
      vec2 clip = zeroToOne * 2.0 - 1.0;
      gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
    }`,
  );
  const fragment = createShader(
    gl,
    gl.FRAGMENT_SHADER,
    `#version 300 es
    precision highp float;
    uniform vec4 u_color;
    out vec4 outColor;
    void main() {
      outColor = u_color;
    }`,
  );

  if (!vertex || !fragment) return null;
  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }
  return program;
}

export function buildEnvelopeColumns(
  data: Float32Array | number[],
  startSample: number,
  endSample: number,
  pixelCount: number,
): { min: Float32Array; max: Float32Array } {
  const min = new Float32Array(pixelCount);
  const max = new Float32Array(pixelCount);
  min.fill(Number.POSITIVE_INFINITY);
  max.fill(Number.NEGATIVE_INFINITY);

  const boundedStart = Math.max(0, startSample);
  const boundedEnd = Math.min(data.length, Math.max(boundedStart, endSample));
  const span = Math.max(1, boundedEnd - boundedStart);

  for (let x = 0; x < pixelCount; x++) {
    const s0 = Math.floor(boundedStart + (x / pixelCount) * span);
    const s1 = Math.min(
      boundedEnd,
      Math.ceil(boundedStart + ((x + 1) / pixelCount) * span),
    );
    if (s1 <= s0) {
      min[x] = 0;
      max[x] = 0;
      continue;
    }
    let localMin = Number.POSITIVE_INFINITY;
    let localMax = Number.NEGATIVE_INFINITY;
    for (let i = s0; i < s1; i++) {
      const v = Number(data[i]);
      if (v < localMin) localMin = v;
      if (v > localMax) localMax = v;
    }
    min[x] = Number.isFinite(localMin) ? localMin : 0;
    max[x] = Number.isFinite(localMax) ? localMax : 0;
  }

  return { min, max };
}

export class WebGLRendererAdapter {
  private readonly canvas: HTMLCanvasElement;
  private readonly gl: WebGL2RenderingContext | null;
  private readonly program: WebGLProgram | null;
  private readonly positionLoc: number;
  private readonly resolutionLoc: WebGLUniformLocation | null;
  private readonly colorLoc: WebGLUniformLocation | null;
  private readonly buffer: WebGLBuffer | null;
  private vertexScratch: Float32Array = new Float32Array(0);
  private vertexBufferBytes = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const candidate = canvas.getContext("webgl2") as
      | WebGL2RenderingContext
      | null;
    const hasRequiredApi =
      !!candidate &&
      typeof candidate.createShader === "function" &&
      typeof candidate.createProgram === "function" &&
      typeof candidate.getAttribLocation === "function" &&
      typeof candidate.getUniformLocation === "function" &&
      typeof candidate.createBuffer === "function";
    this.gl = hasRequiredApi ? candidate : null;
    this.program = this.gl ? createProgram(this.gl) : null;
    this.positionLoc =
      this.gl && this.program
        ? this.gl.getAttribLocation(this.program, "a_position")
        : -1;
    this.resolutionLoc =
      this.gl && this.program
        ? this.gl.getUniformLocation(this.program, "u_resolution")
        : null;
    this.colorLoc =
      this.gl && this.program
        ? this.gl.getUniformLocation(this.program, "u_color")
        : null;
    this.buffer = this.gl ? this.gl.createBuffer() : null;
  }

  isSupported(): boolean {
    return !!this.gl && !!this.program && !!this.buffer;
  }

  render(input: WebGLRenderInput): boolean {
    if (!this.gl || !this.program || !this.buffer) return false;
    const gl = this.gl;
    gl.viewport(0, 0, input.width, input.height);

    const clear = input.clear || [1, 1, 1, 1];
    gl.clearColor(clear[0], clear[1], clear[2], clear[3]);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const xOffset = input.xOffset || 0;
    let requiredFloats = 0;
    for (const ch of input.channels) {
      const width = Math.min(ch.yMin.length, ch.yMax.length);
      requiredFloats += width * 4;
    }
    if (requiredFloats === 0) return true;

    if (this.vertexScratch.length < requiredFloats) {
      const nextCapacity = Math.max(
        requiredFloats,
        Math.ceil(requiredFloats * 1.25),
      );
      this.vertexScratch = new Float32Array(nextCapacity);
    }

    const vertices = this.vertexScratch;
    let vertexOffset = 0;
    for (const ch of input.channels) {
      const width = Math.min(ch.yMin.length, ch.yMax.length);
      for (let x = 0; x < width; x++) {
        const drawX = x + xOffset;
        vertices[vertexOffset++] = drawX;
        vertices[vertexOffset++] = ch.yMin[x];
        vertices[vertexOffset++] = drawX;
        vertices[vertexOffset++] = ch.yMax[x];
      }
    }

    gl.useProgram(this.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    if (this.vertexBufferBytes < this.vertexScratch.byteLength) {
      // Keep one GPU buffer alive across frames instead of orphaning storage on
      // every render; this reduces large upload stalls on the Chrome GPU thread.
      gl.bufferData(gl.ARRAY_BUFFER, this.vertexScratch.byteLength, gl.STREAM_DRAW);
      this.vertexBufferBytes = this.vertexScratch.byteLength;
    }
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, vertices.subarray(0, requiredFloats));
    gl.enableVertexAttribArray(this.positionLoc);
    gl.vertexAttribPointer(this.positionLoc, 2, gl.FLOAT, false, 0, 0);

    if (this.resolutionLoc) {
      gl.uniform2f(this.resolutionLoc, input.width, input.height);
    }
    const color = input.color || [0.145, 0.388, 0.922, 1];
    if (this.colorLoc) {
      gl.uniform4f(this.colorLoc, color[0], color[1], color[2], color[3]);
    }
    gl.drawArrays(gl.LINES, 0, requiredFloats / 2);
    return true;
  }

  dispose(): void {
    if (!this.gl) return;
    if (this.buffer) {
      this.gl.deleteBuffer(this.buffer);
    }
    if (this.program) {
      this.gl.deleteProgram(this.program);
    }
    this.gl.getExtension("WEBGL_lose_context")?.loseContext();
  }
}

export default WebGLRendererAdapter;
